import { StyleSheet, Text, View } from "react-native";

import { Icon, type IconName } from "../Icon/Icon";
import { scaled } from "../pictureScale";
import type { UiTheme } from "../theme";

// <Badge> — THE static status tag (CN-4 census: TimelineList's prTag /
// regressionTag / failTag, AthleteCardRow's flags). A small non-interactive
// pill: tinted background + short label and/or a leading icon. NOT <Chip> —
// Chip is an interactive Pressable toggle with accessibilityState.selected; a
// status badge is display-only and must not claim a control role. Introduced
// with the coach console (CO-8, priority tags on athlete rows).
//
// The label word already carries the meaning, so the tint only reinforces it
// (never colour-alone); an icon adds a second, non-colour cue. A badge with no
// visible label MUST pass a11yLabel — the prop types make the silent
// icon-only-with-no-name case unwriteable rather than a rule to remember.
//
// IB-11 (2026-07-14): the PR trophy was invisible in dark because TimelineList
// hand-rolled its pill — the icon's colour and the pill's background were set
// at two independent places and drifted apart (inkOnBrand, which belongs on
// `brand`, ended up on `surface2`). TONE_PAIRS below is the structural answer:
// background, border and foreground are read from ONE entry, and the icon takes
// its colour from that same entry, so an unreadable combination is not
// expressible from a call site.
//
// There is deliberately no `glyph` prop. A text glyph renders per-device and
// can't take a precise size (Icon.tsx), which is exactly how the timeline's
// «⬇️» ended up the wrong shape and colour (IB-10).

export type BadgeTone = "neutral" | "ok" | "pr" | "warn" | "danger";

// Filled tones (attention-grabbing): solid colour + on-brand ink.
// Soft tones (quieter): tinted fill + coloured/quiet ink.
const TONE_PAIRS = (p: UiTheme["palette"]) =>
  ({
    // borderStrong, not border: a neutral pill sits on surface2, which barely
    // separates from the `surface` card behind it (~1.1:1 in EVERY family —
    // this stack has always leaned on the outline, not the fill). `border`
    // reads 1.73:1 there, so the pill shape dissolved into loose text;
    // borderStrong is what marks a boundary. Found reviewing the DS-8 comfort
    // pass on the coach dashboard's "no program" flag.
    neutral: { bg: p.surface2, fg: p.ink2, border: p.borderStrong },
    ok: { bg: p.completeFill, fg: p.complete, border: p.complete },
    // PR is GOLD across the whole app — RecentPrsStrip, SessionSummary,
    // EffortMeter's PR pulse, IconButton (DS-14) — and the palette carries
    // pr/prFill for exactly this. `ok` green already means "on target" on the
    // coach dashboard, so a green trophy would say the wrong thing.
    pr: { bg: p.prFill, fg: p.pr, border: p.pr },
    warn: { bg: p.warn, fg: p.inkOnWarn, border: "transparent" },
    danger: { bg: p.danger, fg: p.inkOnDanger, border: "transparent" },
  }) satisfies Record<BadgeTone, { bg: string; fg: string; border: string }>;

type BadgeBase = {
  theme: UiTheme;
  tone?: BadgeTone;
  /** Leading icon, coloured from the tone. Never pass a colour for it. */
  icon?: IconName;
  /** The label is a number («8-12»): tabular digits, like every other figure. */
  numeric?: boolean;
};

export type BadgeProps =
  // Labelled: the word carries the meaning; a11yLabel only when the label
  // itself contains a symbol that must not be vocalised raw.
  | (BadgeBase & { label: string; a11yLabel?: string })
  // Icon-only: no visible word, so the accessible name is REQUIRED.
  | (BadgeBase & { icon: IconName; label?: undefined; a11yLabel: string });

export function Badge(props: BadgeProps) {
  const { theme, tone = "neutral", icon, label, numeric = false } = props;
  const styles = makeStyles(theme);
  const { fg } = TONE_PAIRS(theme.palette)[tone];
  return (
    <View
      // `accessible` is what groups the pill into ONE node and lets the
      // container's label replace what the children would each announce.
      // Without it the fail badge read its raw "✕ Failed" text instead of
      // "failed set" (RN only makes touchables accessible by default).
      accessible={!!props.a11yLabel}
      style={[styles.badge, styles[`bg_${tone}`]]}
      accessibilityLabel={label ? props.a11yLabel : undefined}
    >
      {icon ? (
        <Icon
          name={icon}
          size={theme.typography.sizes.sm}
          color={fg}
          // With a visible label the text is the accessible name and the icon
          // is decoration; alone, the icon IS the name.
          label={label ? undefined : props.a11yLabel}
        />
      ) : null}
      {label ? (
        <Text style={[styles.label, styles[`fg_${tone}`], numeric && styles.numeric]} numberOfLines={1}>
          {label}
        </Text>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  const pairs = TONE_PAIRS(p);
  return StyleSheet.create({
    badge: {
      alignSelf: "flex-start",
      flexDirection: "row",
      alignItems: "center",
      gap: s.s1,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      borderRadius: r.r4,
      borderWidth: scaled(theme, 1),
      borderColor: "transparent",
    },
    label: {
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    numeric: { fontVariant: ["tabular-nums"] },
    bg_neutral: { backgroundColor: pairs.neutral.bg, borderColor: pairs.neutral.border },
    fg_neutral: { color: pairs.neutral.fg },
    bg_ok: { backgroundColor: pairs.ok.bg, borderColor: pairs.ok.border },
    fg_ok: { color: pairs.ok.fg },
    bg_pr: { backgroundColor: pairs.pr.bg, borderColor: pairs.pr.border },
    fg_pr: { color: pairs.pr.fg },
    bg_warn: { backgroundColor: pairs.warn.bg, borderColor: pairs.warn.border },
    fg_warn: { color: pairs.warn.fg },
    bg_danger: { backgroundColor: pairs.danger.bg, borderColor: pairs.danger.border },
    fg_danger: { color: pairs.danger.fg },
  });
}
